import type { components } from '~/api/openapi';

type ImageVariant = components['schemas']['ImageUploadUrlDto']['availableVariants'][number];

export function getVariantImage(image = '', variant: ImageVariant = 'original') {
  if (!image || !variant) {
    return '';
  }

  // extract the base url from specific variant in case a specific variant was called like the following:
  // 'https://imagedelivery.net/duESwD0YdBSQAUgzkvSZXg/b957ee4e-32b3-45da-6033-2b31793dc100/original'
  const IMAGE_URL_WITH_VARIANT = /https:\/\/imagedelivery\.net\/([^/]+)\/([^/]+)\/([^/]+)/;

  if (image.match(IMAGE_URL_WITH_VARIANT)) {
    //replace the variant part with the new variant
    return image.replace(IMAGE_URL_WITH_VARIANT, `https://imagedelivery.net/$1/$2/${variant}`);
  }

  return `${image}/${variant}`;
}

/**
 * Generates a Cloudflare Image URL with width transformation parameter
 * Handles URLs that may already have a variant appended
 * @param image - Base image URL (may include variant)
 * @param width - Desired width in pixels
 * @returns URL with width transformation applied, or original URL if not a Cloudflare image
 */
export function getCloudflareImageUrl(image: string, width: number): string {
  if (!image) {
    return '';
  }

  // Pattern matches Cloudflare Image Delivery URLs with or without variants
  // Handles both:
  // - https://imagedelivery.net/account/imageId
  // - https://imagedelivery.net/account/imageId/variant
  const IMAGE_URL_PATTERN = /https:\/\/imagedelivery\.net\/([^/]+)\/([^/]+)/;
  const match = image.match(IMAGE_URL_PATTERN);

  if (match) {
    const [, accountHash, imageId] = match;
    return `https://imagedelivery.net/${accountHash}/${imageId}/w=${width}`;
  }

  // Fallback: return original URL unchanged for non-Cloudflare images
  return image;
}

/**
 * Generates srcset attribute for responsive images on high-DPI displays
 * @param baseUrl - Base image URL
 * @param width - Base width for 1x display
 * @returns srcset string for 1x and 2x displays
 */
export function getResponsiveSrcSet(baseUrl: string, width: number): string {
  if (!baseUrl) {
    return '';
  }

  const url1x = getCloudflareImageUrl(baseUrl, width);
  const url2x = getCloudflareImageUrl(baseUrl, width * 2);
  return `${url1x} 1x, ${url2x} 2x`;
}

/**
 * Generates srcset with width descriptors for fluid responsive layouts
 * @param baseUrl - Base image URL
 * @param widths - Array of widths to generate (e.g., [320, 480, 640, 960])
 * @returns srcset string with width descriptors
 */
/**
 * Strip the variant suffix from a Cloudflare Image Delivery URL.
 * e.g. https://imagedelivery.net/acct/img/original → https://imagedelivery.net/acct/img
 * Non-Cloudflare URLs are returned unchanged.
 */
export function stripCloudflareVariant(url: string): string {
  const match = url.match(/^(https:\/\/imagedelivery\.net\/[^/]+\/[^/]+)\/.+$/);
  return match?.[1] ?? url;
}

export function getFluidSrcSet(baseUrl: string, widths: number[]): string {
  if (!baseUrl) {
    return '';
  }

  return widths.map(width => `${getCloudflareImageUrl(baseUrl, width)} ${width}w`).join(', ');
}
